/**
 * How large a Scratchpad is allowed to be, in one place because the client and the
 * Jot Server state the same rule and each stating it itself is how they disagreed.
 *
 * The rule: **a body the Jot Server accepts has to be one a fresh device can read
 * back.** Break it and the failure is silent and permanent in one direction only —
 * the device that pushed the oversized body is fine, and every device that has not
 * paired yet is refused by its own response cap on the pairing `GET`, so it records
 * nothing, pushes nothing and never converges. Exactly the shape of the `connect-src`
 * failure `loopback.ts` exists to prevent: one side approves what the other silently
 * refuses.
 *
 * Deliberately free of imports, and it has to stay that way. The Jot Server runs its
 * TypeScript through Node's type stripping with no bundler and no build step, and it
 * reaches this file by relative path across the workspace boundary — so anything this
 * module imported would have to resolve under Node's own rules as well as the app's
 * bundler, which is the constraint that keeps `sync-wire.ts` types-only. A number and
 * its reasons need nothing, so this one can be shared as a value.
 *
 * That is also the whole of why `src/shared/package.json` exists and says nothing but
 * `type: module`. The app's root package is type-less, which makes every `.ts` under it
 * CommonJS to Node and to any project resolving as Node does — so the server's
 * `nodenext` build rejects a value `export` from here, and Node reparses the file with
 * a warning on every start. One line fixes both, and it is inert for the bundled
 * builds, which decide module format from syntax and never consult it.
 */

/**
 * Ceiling on a Scratchpad arriving from a Jot Server: on the `GET` response, on the
 * `409` body, and so on the Pending Conflict either of them may be persisted as.
 *
 * Bounded at all because a Pending Conflict is persisted, and `sync-state.json` is
 * `readFileSync`+`JSON.parse`d before the panel can open — an unbounded response is
 * an unbounded cost on every launch thereafter. A body can never exceed the JSON
 * that carried it, so a record the client accepted is always one Sync State will
 * store: the cap can only ever fire at the outer edge, where it means "that answer
 * did not fit the contract" rather than "Sync State could not be written".
 *
 * The anchor of the two numbers below, rather than the derived one, because it is
 * the only one a device decides for itself — every other cap here exists to keep a
 * server inside it.
 */
export const MAX_SCRATCHPAD_BYTES = 8 * 1024 * 1024

/**
 * What a `GET` response carries beyond the `PUT` that created it.
 *
 * Both caps measure the same kind of thing, which is what makes a fixed slack the
 * honest shape: the server's cap is on the whole `PUT` request body (`readBody` in
 * `server/src/http.ts`) and the client's is on the whole response (`readCapped` in
 * `sync/client.ts`) — two JSON envelopes around one record. So a response is not
 * some multiple of the request that produced it; it is the same record plus
 * `updatedAt`, and `JSON.stringify` escapes minimally, so no sender can encode a
 * string in fewer bytes than the server re-emits it in.
 *
 * That last claim is about *text*, and it is only a claim about bytes while the
 * server refuses a request body that is not valid UTF-8 (`readBody` in
 * `server/src/http.ts`). A lossy decode substitutes U+FFFD for each invalid byte,
 * three bytes where one arrived, so an invalid request would be re-emitted up to
 * three times its own size — and a fixed slack absorbs an addition, never a
 * multiple. Which is why that refusal is a cap rule and not input validation.
 *
 * The derivation this replaces multiplied by eight to leave room for escaping, which
 * was a body-versus-envelope category error — it capped an envelope as though the
 * number were a cap on the raw markdown, and then reserved room to escape the same
 * bytes a second time. The cost was never the slack; it was the ceiling that fell
 * out of it, below.
 */
const RESPONSE_HEADROOM_BYTES = 64 * 1024

/**
 * Ceiling on a `PUT` request, and so on `JOT_MAX_BODY_BYTES`: the server refuses to
 * start above it (`server/src/config.ts`) rather than accepting what its own clients
 * could not read back, and the client refuses to send above it (`sync/client.ts`)
 * rather than spending a round trip to learn the same thing from a `413`.
 *
 * Two things keep it, and the number alone is only one of them: the server refuses
 * to start above it, and it refuses a `PUT` body that is not valid UTF-8 — because a
 * request it decoded lossily would leave here larger than it arrived, and the
 * subtraction above holds a response to being the same record plus `updatedAt`.
 *
 * A ceiling and not a default — `DEFAULT_PUSH_BYTES` is the default, and it is far
 * lower. Which is the whole difference between this number and the one it replaces:
 * unifying the two caps into a single rule was right, and taking the *smaller* of
 * them as the shared value refused to start deployments that were demonstrably
 * healthy. A server configured for 4 MiB with a 2 MiB Scratchpad pushes, and a fresh
 * device's pairing `GET` reads that body back, because the response cap above has
 * been 8 MiB throughout. For those installs the only supported move would have been
 * to lower the cap under a Scratchpad no device could then ever push again.
 */
export const MAX_PUSH_BYTES = MAX_SCRATCHPAD_BYTES - RESPONSE_HEADROOM_BYTES

/**
 * `JOT_MAX_BODY_BYTES` when nobody sets it, and deliberately nowhere near the
 * ceiling: an exposed `PUT` is an unbounded upload from anyone who finds it
 * (ADR-0003), so the default is the conservative number and a deployment that needs
 * more says so. Raising the ceiling must not raise this.
 */
export const DEFAULT_PUSH_BYTES = 1024 * 1024
